/**
 * Кодировщик QR — ровно столько стандарта, сколько нужно для строки otpauth://.
 *
 * ПОЧЕМУ СВОЙ, А НЕ БИБЛИОТЕКА. Ссылка на второй фактор содержит СЕКРЕТ. Отдать её
 * рисовать чужому коду означает: (1) держать в контуре второго фактора зависимость,
 * которую надо обновлять и проверять по minimumReleaseAge; (2) ради одной картинки
 * тянуть в сборку кабинета пакет с десятком транзитивных. Здесь нужен байтовый режим
 * и уровень M — это триста строк без зависимостей, и они целиком проверяемы.
 *
 * НЕВЕРНЫЙ QR НИКОГО НЕ ЗАПИРАЕТ, и это свойство устройства пакета, а не удача: фактор
 * включается только после того, как код ИЗ ПРИЛОЖЕНИЯ сошёлся с выпущенным секретом.
 * Картинка, которую приложение не прочитало или прочитало неверно, даёт «код не подошёл»
 * на следующем шаге — то есть отказ до включения, а не запертую учётку после.
 *
 * ЧТО НЕ РЕАЛИЗОВАНО СОЗНАТЕЛЬНО: числовой и буквенно-цифровой режимы (короче байтового,
 * но для URI неприменимы), версии старше 10 (v10 уровня M — это 216 байт, ссылка
 * otpauth занимает около 130), уровни L/Q/H (M — то, что ждут приложения), кандзи.
 *
 * Проверка — сравнением МАТРИЦ с независимой реализацией (segno 1.6.6): см. qr.test.ts.
 * Совпадение до модуля означает, что и выбор маски, и коррекция ошибок, и раскладка
 * совпали со стандартом, а не «похожи».
 */

/** Уровень коррекции M: столько данных влезает в версию (кодовых слов). */
const DATA_CODEWORDS_M: readonly number[] = [16, 28, 44, 64, 86, 108, 124, 154, 182, 216]

/** Блоки коррекции уровня M: [слов коррекции на блок, [размеры блоков данных]]. */
const BLOCKS_M: readonly (readonly [number, readonly number[]])[] = [
  [10, [16]],
  [16, [28]],
  [26, [44]],
  [18, [32, 32]],
  [24, [43, 43]],
  [16, [27, 27, 27, 27]],
  [18, [31, 31, 31, 31]],
  [22, [38, 38, 39, 39]],
  [22, [36, 36, 36, 37, 37]],
  [26, [43, 43, 43, 43, 44]],
]

/** Координаты центров выравнивающих узоров по версиям (v1 их не имеет). */
const ALIGNMENT: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
]

/**
 * Матрица QR: true — тёмный модуль.
 *
 * Возвращает квадрат БЕЗ тихой зоны: отступ — дело того, кто рисует, и в SVG он делается
 * полями, а не строками матрицы.
 */
export function qrMatrix(text: string): readonly (readonly boolean[])[] {
  const data = new TextEncoder().encode(text)
  const version = versionFor(data.length)
  const codewords = codewordsFor(data, version)
  const size = 17 + version * 4

  // Функциональные модули отмечаются ЗАРАНЕЕ и отдельно от цвета: по этой карте
  // и раскладываются данные, и накладывается маска. Один массив на два смысла
  // («тёмный» и «служебный») давал бы данные поверх узоров поиска.
  const reserved = Array.from({ length: size }, () => Array.from({ length: size }, () => false))
  const modules = Array.from({ length: size }, () => Array.from({ length: size }, () => false))
  const put = (row: number, column: number, dark: boolean): void => {
    modules[row]![column] = dark
    reserved[row]![column] = true
  }

  drawFinders(size, put)
  drawTiming(size, put)
  drawAlignment(version, put)
  reserveFormat(size, reserved)
  // Сведения о версии рисуются ДО выбора маски: это такой же функциональный узор,
  // как узоры поиска, и он входит в оценку узорности картинки.
  if (version >= 7) {
    drawVersion(version, size, put)
  }

  placeData(codewords, size, reserved, modules)

  const mask = bestMask(size, reserved, modules, put)
  applyMask(mask, size, reserved, modules)
  drawFormat(mask, size, put)
  return modules
}

/** Самая маленькая версия, в которую влезает строка. */
function versionFor(length: number): number {
  for (let version = 1; version <= DATA_CODEWORDS_M.length; version += 1) {
    const countBits = version < 10 ? 8 : 16
    const needed = Math.ceil((4 + countBits + length * 8) / 8)
    if (needed <= (DATA_CODEWORDS_M[version - 1] ?? 0)) {
      return version
    }
  }
  throw new Error('строка не помещается в QR уровня M версии 10')
}

/** Данные + коррекция в порядке записи в матрицу (с перемежением блоков). */
function codewordsFor(data: Uint8Array, version: number): readonly number[] {
  const capacity = DATA_CODEWORDS_M[version - 1] ?? 0
  const bits: number[] = []
  const push = (value: number, width: number): void => {
    for (let shift = width - 1; shift >= 0; shift -= 1) {
      bits.push((value >>> shift) & 1)
    }
  }
  push(0b0100, 4) // байтовый режим
  push(data.length, version < 10 ? 8 : 16)
  for (const byte of data) {
    push(byte, 8)
  }
  // Признак конца — до четырёх нулей, но не больше, чем осталось места.
  for (let i = 0; i < 4 && bits.length < capacity * 8; i += 1) {
    bits.push(0)
  }
  while (bits.length % 8 !== 0) {
    bits.push(0)
  }
  const bytes: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0
    for (let shift = 0; shift < 8; shift += 1) {
      byte = (byte << 1) | (bits[i + shift] ?? 0)
    }
    bytes.push(byte)
  }
  // Добивка чередующимися 0xEC/0x11 — значения из стандарта, смысла не несут.
  for (let i = 0; bytes.length < capacity; i += 1) {
    bytes.push(i % 2 === 0 ? 0xec : 0x11)
  }

  const [eccPerBlock, blockSizes] = BLOCKS_M[version - 1] ?? [0, []]
  const dataBlocks: number[][] = []
  const eccBlocks: number[][] = []
  let offset = 0
  for (const blockSize of blockSizes) {
    const block = bytes.slice(offset, offset + blockSize)
    offset += blockSize
    dataBlocks.push(block)
    eccBlocks.push(reedSolomon(block, eccPerBlock))
  }

  // ПЕРЕМЕЖЕНИЕ: по одному слову из каждого блока по кругу. Смысл в том, чтобы царапина
  // на картинке попортила по чуть-чуть в каждом блоке, а не убила один блок целиком.
  const result: number[] = []
  const longest = Math.max(...blockSizes)
  for (let i = 0; i < longest; i += 1) {
    for (const block of dataBlocks) {
      const value = block[i]
      if (value !== undefined) {
        result.push(value)
      }
    }
  }
  for (let i = 0; i < eccPerBlock; i += 1) {
    for (const block of eccBlocks) {
      result.push(block[i] ?? 0)
    }
  }
  return result
}

// ── Поле Галуа GF(256) ──────────────────────────────────────────────────────
//
// Таблицы логарифмов считаются один раз: примитивный многочлен 0x11D — тот, что задан
// стандартом, другой дал бы формально верную арифметику и неверную коррекцию.

const GF_EXP = new Uint8Array(512)
const GF_LOG = new Uint8Array(256)
{
  let value = 1
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = value
    GF_LOG[value] = i
    value <<= 1
    if (value >= 256) {
      value ^= 0x11d
    }
  }
  for (let i = 255; i < 512; i += 1) {
    GF_EXP[i] = GF_EXP[i - 255] ?? 0
  }
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) {
    return 0
  }
  return GF_EXP[((GF_LOG[a] ?? 0) + (GF_LOG[b] ?? 0)) % 255] ?? 0
}

/** Слова коррекции блока: остаток от деления на порождающий многочлен. */
function reedSolomon(block: readonly number[], count: number): number[] {
  let generator = [1]
  for (let i = 0; i < count; i += 1) {
    const next: number[] = Array.from({ length: generator.length + 1 }, () => 0)
    for (let j = 0; j < generator.length; j += 1) {
      next[j] = (next[j] ?? 0) ^ (generator[j] ?? 0)
      next[j + 1] = (next[j + 1] ?? 0) ^ gfMul(generator[j] ?? 0, GF_EXP[i] ?? 0)
    }
    generator = next
  }
  const remainder: number[] = Array.from({ length: count }, () => 0)
  for (const byte of block) {
    const factor = byte ^ (remainder[0] ?? 0)
    remainder.shift()
    remainder.push(0)
    if (factor !== 0) {
      for (let i = 0; i < count; i += 1) {
        remainder[i] = (remainder[i] ?? 0) ^ gfMul(generator[i + 1] ?? 0, factor)
      }
    }
  }
  return remainder
}

// ── Функциональные узоры ────────────────────────────────────────────────────

type Put = (row: number, column: number, dark: boolean) => void

/** Три узора поиска с разделителями: по ним сканер находит и ориентирует картинку. */
function drawFinders(size: number, put: Put): void {
  for (const [top, left] of [
    [0, 0],
    [0, size - 7],
    [size - 7, 0],
  ] as const) {
    for (let row = -1; row <= 7; row += 1) {
      for (let column = -1; column <= 7; column += 1) {
        const r = top + row
        const c = left + column
        if (r < 0 || r >= size || c < 0 || c >= size) {
          continue
        }
        const ring = Math.max(Math.abs(row - 3), Math.abs(column - 3))
        // Разделитель (ring === 4) — светлый и тоже служебный: данные в него не идут.
        put(r, c, ring !== 2 && ring !== 4 && row >= 0 && row <= 6 && column >= 0 && column <= 6)
      }
    }
  }
}

/** Линии синхронизации: чередование по шестой строке и шестому столбцу. */
function drawTiming(size: number, put: Put): void {
  for (let i = 8; i < size - 8; i += 1) {
    const dark = i % 2 === 0
    put(6, i, dark)
    put(i, 6, dark)
  }
}

function drawAlignment(version: number, put: Put): void {
  const centers = ALIGNMENT[version - 1] ?? []
  const last = centers.length - 1
  for (let i = 0; i <= last; i += 1) {
    for (let j = 0; j <= last; j += 1) {
      /*
       * НЕ РИСУЮТСЯ РОВНО ТРИ УГЛА — те, где стоят узоры поиска. Исключение задано
       * НОМЕРАМИ, а не проверкой «место уже занято»: занятой оказывается и середина
       * линии синхронизации, и по такой проверке молча пропадали два узора из девяти
       * (у версий 7 и старше центр 6 лежит на линии). Картинка при этом выглядит
       * правильной, а данных в ней на сорок бит больше, чем должно быть, — то есть
       * не читается вовсе. Поймано сравнением с эталоном.
       */
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) {
        continue
      }
      const row = centers[i] ?? 0
      const column = centers[j] ?? 0
      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          const ring = Math.max(Math.abs(dr), Math.abs(dc))
          put(row + dr, column + dc, ring !== 1)
        }
      }
    }
  }
}

/** Места под сведения о формате: цвет появится после выбора маски. */
function reserveFormat(size: number, reserved: boolean[][]): void {
  for (let i = 0; i <= 8; i += 1) {
    if (i !== 6) {
      reserved[8]![i] = true
      reserved[i]![8] = true
    }
  }
  for (let i = 0; i < 8; i += 1) {
    reserved[8]![size - 1 - i] = true
    reserved[size - 1 - i]![8] = true
  }
}

/**
 * Раскладка данных: змейкой по столбцам шириной два, справа налево, снизу вверх
 * и обратно. Шестой столбец пропускается целиком — он занят линией синхронизации.
 */
function placeData(
  codewords: readonly number[],
  size: number,
  reserved: readonly (readonly boolean[])[],
  modules: boolean[][],
): void {
  let bit = 0
  const total = codewords.length * 8
  let upward = true
  for (let right = size - 1; right >= 1; right -= 2) {
    const column = right <= 6 ? right - 1 : right
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step
      for (const c of [column, column - 1]) {
        if (c < 0 || reserved[row]?.[c] === true) {
          continue
        }
        // Биты за пределами данных — светлые: это остаточные биты версии.
        const value = bit < total ? ((codewords[bit >>> 3] ?? 0) >>> (7 - (bit & 7))) & 1 : 0
        modules[row]![c] = value === 1
        bit += 1
      }
    }
    upward = !upward
  }
}

/** Правило маски: true — модуль переворачивается. */
function maskAt(mask: number, row: number, column: number): boolean {
  switch (mask) {
    case 0:
      return (row + column) % 2 === 0
    case 1:
      return row % 2 === 0
    case 2:
      return column % 3 === 0
    case 3:
      return (row + column) % 3 === 0
    case 4:
      return (Math.floor(row / 2) + Math.floor(column / 3)) % 2 === 0
    case 5:
      return ((row * column) % 2) + ((row * column) % 3) === 0
    case 6:
      return (((row * column) % 2) + ((row * column) % 3)) % 2 === 0
    default:
      return (((row + column) % 2) + ((row * column) % 3)) % 2 === 0
  }
}

function applyMask(
  mask: number,
  size: number,
  reserved: readonly (readonly boolean[])[],
  modules: boolean[][],
): void {
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      if (reserved[row]?.[column] !== true && maskAt(mask, row, column)) {
        modules[row]![column] = !modules[row]![column]
      }
    }
  }
}

/**
 * Выбор маски по четырём штрафам стандарта.
 *
 * Считать их обязательно, а не брать маску 0: сканер читает любую правильную маску,
 * но выбор «худшей» даёт картинку с длинными одноцветными полосами, которую камера
 * телефона в полумраке салона читает через раз. Тот же расчёт делает и эталонная
 * реализация, поэтому матрицы совпадают до модуля.
 */
function bestMask(
  size: number,
  reserved: readonly (readonly boolean[])[],
  modules: boolean[][],
  put: Put,
): number {
  let best = 0
  let bestPenalty = Number.POSITIVE_INFINITY
  for (let mask = 0; mask < 8; mask += 1) {
    applyMask(mask, size, reserved, modules)
    // Сведения о формате обязаны стоять на месте ДО подсчёта: пятнадцать модулей рядом
    // с узором поиска дают и полосы, и квадраты 2×2, то есть влияют на штраф. Без них
    // выбор маски расходится с любой правильной реализацией — проверено эталоном.
    drawFormat(mask, size, put)
    const penalty = penaltyOf(size, modules)
    applyMask(mask, size, reserved, modules)
    if (penalty < bestPenalty) {
      bestPenalty = penalty
      best = mask
    }
  }
  return best
}

/**
 * Штраф картинки по четырём правилам стандарта (ISO/IEC 18004, 7.8.3).
 *
 * ПЕРЕНЕСЕНО С ЭТАЛОННОЙ РЕАЛИЗАЦИИ ПОЧТИ ДОСЛОВНО, и на то есть две причины, обе
 * выяснились сравнением матриц:
 *
 * 1. ГРАНИЦА КАРТИНКИ СЧИТАЕТСЯ СВЕТЛОЙ. Правило 3 ищет узор «как у искателя» вместе
 *    с четырьмя светлыми модулями рядом. У края этих модулей нет — но есть тихая зона,
 *    и она светлая. Реализация, которая просто не находит узор у края, выбирает другую
 *    маску, чем все остальные.
 *
 * 2. ПРАВИЛО 4 СЧИТАЕТСЯ ЧЕРЕЗ ЦЕЛЫЕ ЧИСЛА И ОКРУГЛЕНИЕ ВВЕРХ. «Целая часть от
 *    отклонения, делённого на пять» и «наименьшее k, при котором доля попадает
 *    в 50 % ± 5(k+1)» расходятся ровно на кратных пяти процентах — и этого достаточно,
 *    чтобы выбрать другую маску.
 */
function penaltyOf(size: number, modules: readonly (readonly boolean[])[]): number {
  let penalty = 0

  // Правила 1 и 3 — одним проходом по строкам и одним по столбцам.
  for (let i = 0; i < size; i += 1) {
    for (const at of [
      (j: number): boolean => modules[i]?.[j] === true,
      (j: number): boolean => modules[j]?.[i] === true,
    ]) {
      let runColor = false
      let runLength = 0
      const history = [0, 0, 0, 0, 0, 0, 0]
      const remember = (length: number): void => {
        // Ноль в начале истории означает «ещё ни одной полосы не было»: перед первой
        // полосой лежит тихая зона, и её ширина добавляется к длине.
        history.pop()
        history.unshift(history[0] === 0 ? length + size : length)
      }
      for (let j = 0; j < size; j += 1) {
        if (at(j) === runColor) {
          runLength += 1
          // Правило 1: пять в ряд — три очка, каждый следующий — ещё одно.
          if (runLength === 5) {
            penalty += 3
          } else if (runLength > 5) {
            penalty += 1
          }
          continue
        }
        remember(runLength)
        // Узор ищется только когда закончилась СВЕТЛАЯ полоса: иначе та же
        // последовательность посчиталась бы дважды.
        if (!runColor) {
          penalty += finderLike(history) * 40
        }
        runColor = at(j)
        runLength = 1
      }
      if (runColor) {
        remember(runLength)
        runLength = 0
      }
      remember(runLength + size)
      penalty += finderLike(history) * 40
    }
  }

  // Правило 2: каждый одноцветный квадрат 2×2 — три очка.
  for (let row = 0; row < size - 1; row += 1) {
    for (let column = 0; column < size - 1; column += 1) {
      const value = modules[row]?.[column]
      if (
        value === modules[row]?.[column + 1] &&
        value === modules[row + 1]?.[column] &&
        value === modules[row + 1]?.[column + 1]
      ) {
        penalty += 3
      }
    }
  }

  // Правило 4: перекос светлого и тёмного — десять очков за каждые лишние пять
  // процентов отклонения от половины.
  let dark = 0
  for (let row = 0; row < size; row += 1) {
    for (let column = 0; column < size; column += 1) {
      if (modules[row]?.[column] === true) {
        dark += 1
      }
    }
  }
  const total = size * size
  penalty += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10

  return penalty
}

/**
 * Сколько раз в истории полос виден узор 1:1:3:1:1 со светлой зоной — ноль, один
 * или два (зона может быть с любой стороны, а может с обеих).
 */
function finderLike(history: readonly number[]): number {
  const n = history[1] ?? 0
  const core =
    n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n
  const before = core && (history[0] ?? 0) >= n * 4 && (history[6] ?? 0) >= n
  const after = core && (history[6] ?? 0) >= n * 4 && (history[0] ?? 0) >= n
  return (before ? 1 : 0) + (after ? 1 : 0)
}

/** Сведения о формате: уровень M и номер маски, защищённые кодом БЧХ. */
function drawFormat(mask: number, size: number, put: Put): void {
  // Уровень M — это нули в старших двух битах; ниже три бита маски. 0x5412 —
  // обязательная маска стандарта: без неё нулевые сведения дали бы пустой узор,
  // неотличимый от светлого поля.
  const data = (0b00 << 3) | mask
  const value = ((data << 10) | bch(data, 0x537, 10)) ^ 0x5412
  const bitAt = (index: number): boolean => ((value >>> index) & 1) === 1

  /*
   * РАСКЛАДКА ЗДЕСЬ НЕСИММЕТРИЧНА, И ПЕРЕПУТАТЬ СТРОКУ СО СТОЛБЦОМ — САМАЯ ЛЁГКАЯ
   * ОШИБКА ВО ВСЁМ ФАЙЛЕ. Первая копия: младшие биты идут ВНИЗ ПО СТОЛБЦУ 8, старшие —
   * ВЛЕВО ПО СТРОКЕ 8. Зеркальная запись даёт картинку, которая выглядит как QR, имеет
   * верные узоры поиска и не читается ни одним приложением. Поймано сравнением
   * с эталоном (qr.test.ts) — глазами такое не видно.
   */
  for (let i = 0; i <= 5; i += 1) {
    put(i, 8, bitAt(i))
  }
  put(7, 8, bitAt(6))
  put(8, 8, bitAt(7))
  put(8, 7, bitAt(8))
  for (let i = 9; i <= 14; i += 1) {
    put(8, 14 - i, bitAt(i))
  }
  // Вторая копия: те же пятнадцать бит у двух других углов — сведения о формате обязаны
  // читаться, даже если один угол картинки залит кофе.
  for (let i = 0; i <= 7; i += 1) {
    put(8, size - 1 - i, bitAt(i))
  }
  for (let i = 8; i <= 14; i += 1) {
    put(size - 15 + i, 8, bitAt(i))
  }
  // Тёмный модуль — ПОСЛЕДНИМ: вторая копия сведений о формате проходит по этой же
  // клетке, и записанный раньше он был бы затёрт своим же битом.
  put(size - 8, 8, true)
}

/** Номер версии для v7 и старше: без него сканер не знает размер сетки. */
function drawVersion(version: number, size: number, put: Put): void {
  const value = (version << 12) | bch(version, 0x1f25, 12)
  for (let i = 0; i < 18; i += 1) {
    const dark = ((value >>> i) & 1) === 1
    put(Math.floor(i / 3), size - 11 + (i % 3), dark)
    put(size - 11 + (i % 3), Math.floor(i / 3), dark)
  }
}

/** Остаток от деления на порождающий многочлен БЧХ. */
function bch(value: number, generator: number, degree: number): number {
  let result = value << degree
  const generatorBits = 32 - Math.clz32(generator)
  while (32 - Math.clz32(result) >= generatorBits) {
    result ^= generator << (32 - Math.clz32(result) - generatorBits)
  }
  return result
}
